/**
 * `clipController.ts` — WP-6 (3D model / BIM / 3D Tiles ToolCard).
 *
 * Pure helpers for the section / clipping slice extracted from
 * `Giro3DToolbar.tsx`. The right-rail `<SectionAndClipToolCard />` and
 * any future engine-side controller import these helpers so the math is
 * tested in isolation (no Three.js bundle, no DOM).
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 *     §7.6 Card 6 (section planes X/Y/Z + custom plane normal,
 *     clipping box axis-aligned + tighten-to-selection);
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5
 *     (single primary CTA per row; non-color cue per state).
 *
 * The spec keeps section plane normals as **vector inputs in MONO**:
 * the typography rule lives in the ToolCard but the canonical normal
 * vector type is exported here so the UI never invents a competing
 * representation.
 */

import type { ModelBounds } from './cameraPresets';

/** Axis-aligned clipping axis (used by both the toolbar and the card). */
export type ClipAxis = 'x' | 'y' | 'z';

/**
 * One section plane spec. `position` is a normalized 0..1 fraction of
 * the model size along the axis (matches the toolbar slider semantics).
 * `customNormal`, when present, replaces the axis-aligned normal — the
 * UI surfaces a Vector3 mono input for it.
 */
export interface SectionPlaneSpec {
  readonly axis: ClipAxis;
  readonly position: number;
  readonly flip: boolean;
  /** Optional custom plane normal (world space, will be normalized). */
  readonly customNormal?: readonly [number, number, number];
}

/** Pure plane representation: normal + signed constant. */
export interface ClipPlaneVector {
  readonly normal: readonly [number, number, number];
  readonly constant: number;
}

/** Default normalized position (mid-axis) — keeps the slider centered. */
export const DEFAULT_CLIP_POSITION = 0.5;

/** Length below which a custom normal is treated as invalid (prevents NaN). */
const MIN_NORMAL_LENGTH = 1e-6;

/**
 * Compute the clip-plane vector for a section spec given the current
 * model bounds. Returns `null` when bounds are missing or the custom
 * normal is degenerate.
 */
export function computeClipPlane(
  spec: SectionPlaneSpec,
  bounds: ModelBounds | null,
): ClipPlaneVector | null {
  if (!bounds) return null;

  const [cx, cy, cz] = bounds.center;
  const [sx, sy, sz] = bounds.size;
  const [normal, ok] = resolveNormal(spec);
  if (!ok) return null;

  const halfSize =
    spec.axis === 'x' ? sx / 2 : spec.axis === 'y' ? sy / 2 : sz / 2;
  const center = spec.axis === 'x' ? cx : spec.axis === 'y' ? cy : cz;
  const planeConst = -(center - halfSize + spec.position * halfSize * 2);
  const constant = spec.flip ? -planeConst : planeConst;

  return { normal, constant };
}

/**
 * Snap a slider value into the canonical [0, 1] range. Used by the
 * card's slider onChange handler so values returned to the engine never
 * drift outside the supported range (Three.js plane math accepts any
 * float, but the toolbar slider is bound to [0, 1]).
 */
export function clampClipPosition(value: number): number {
  if (Number.isNaN(value)) return DEFAULT_CLIP_POSITION;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/**
 * Parse a Vector3-shaped mono input (e.g. `"1, 0, 0"` or whitespace-
 * separated). Used by the SectionAndClipToolCard's custom plane normal
 * field — keeps validation here so the card stays declarative.
 *
 * Returns `null` on parse failure (any non-numeric component, fewer than
 * three components, or zero-length normal).
 */
export function parseNormalInput(
  raw: string,
): readonly [number, number, number] | null {
  if (!raw || typeof raw !== 'string') return null;
  const parts = raw
    .replace(/[[\]()]/g, '')
    .split(/[,\s]+/)
    .filter((part) => part.length > 0);
  if (parts.length !== 3) return null;
  const numbers: number[] = [];
  for (const part of parts) {
    const value = Number(part);
    if (!Number.isFinite(value)) return null;
    numbers.push(value);
  }
  const [nx, ny, nz] = numbers as [number, number, number];
  const length = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (length < MIN_NORMAL_LENGTH) return null;
  return [nx / length, ny / length, nz / length];
}

/**
 * Format a Vector3 for display in the mono input. Six significant
 * digits is enough for hand-tweaked plane normals (Cesium / Navisworks
 * use the same precision in their plane editors).
 */
export function formatNormalInput(
  normal: readonly [number, number, number],
): string {
  const [x, y, z] = normal;
  return `${x.toFixed(6)}, ${y.toFixed(6)}, ${z.toFixed(6)}`;
}

function resolveNormal(
  spec: SectionPlaneSpec,
): readonly [readonly [number, number, number], boolean] {
  if (spec.customNormal) {
    const [nx, ny, nz] = spec.customNormal;
    const length = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (length < MIN_NORMAL_LENGTH) return [[0, 0, 1], false];
    const normalized: readonly [number, number, number] = [
      (spec.flip ? -nx : nx) / length,
      (spec.flip ? -ny : ny) / length,
      (spec.flip ? -nz : nz) / length,
    ];
    return [normalized, true];
  }

  const sign = spec.flip ? -1 : 1;
  if (spec.axis === 'x') return [[sign, 0, 0], true];
  if (spec.axis === 'y') return [[0, sign, 0], true];
  return [[0, 0, sign], true];
}

/**
 * Axis-aligned clipping box descriptor. Each min/max pair is a 0..1
 * normalized fraction of the model bounds along the axis. The
 * SectionAndClipToolCard builds one of these from six sliders or from a
 * "tighten to selection" action; the engine consumes the canonical
 * range when applying clipping planes.
 */
export interface ClippingBoxSpec {
  readonly xMin: number;
  readonly xMax: number;
  readonly yMin: number;
  readonly yMax: number;
  readonly zMin: number;
  readonly zMax: number;
}

/** Default fully-open clipping box (no clipping). */
export const DEFAULT_CLIPPING_BOX: ClippingBoxSpec = {
  xMin: 0,
  xMax: 1,
  yMin: 0,
  yMax: 1,
  zMin: 0,
  zMax: 1,
};

/**
 * Compute the six clip planes for an axis-aligned clipping box. Each
 * face's outward normal points away from the box interior. Returns
 * `null` when bounds are missing or the box is collapsed (any axis
 * with min ≥ max).
 */
export function computeClippingBoxPlanes(
  box: ClippingBoxSpec,
  bounds: ModelBounds | null,
): ClipPlaneVector[] | null {
  if (!bounds) return null;
  if (
    box.xMin >= box.xMax ||
    box.yMin >= box.yMax ||
    box.zMin >= box.zMax
  ) {
    return null;
  }

  const [cx, cy, cz] = bounds.center;
  const [sx, sy, sz] = bounds.size;
  const planes: ClipPlaneVector[] = [];
  const axes: Array<{
    sizeHalf: number;
    center: number;
    minFrac: number;
    maxFrac: number;
    minNormal: [number, number, number];
    maxNormal: [number, number, number];
  }> = [
    {
      sizeHalf: sx / 2,
      center: cx,
      minFrac: box.xMin,
      maxFrac: box.xMax,
      minNormal: [1, 0, 0],
      maxNormal: [-1, 0, 0],
    },
    {
      sizeHalf: sy / 2,
      center: cy,
      minFrac: box.yMin,
      maxFrac: box.yMax,
      minNormal: [0, 1, 0],
      maxNormal: [0, -1, 0],
    },
    {
      sizeHalf: sz / 2,
      center: cz,
      minFrac: box.zMin,
      maxFrac: box.zMax,
      minNormal: [0, 0, 1],
      maxNormal: [0, 0, -1],
    },
  ];

  for (const axis of axes) {
    const minWorld = axis.center - axis.sizeHalf + axis.minFrac * axis.sizeHalf * 2;
    const maxWorld = axis.center - axis.sizeHalf + axis.maxFrac * axis.sizeHalf * 2;
    planes.push({ normal: axis.minNormal, constant: -minWorld });
    planes.push({ normal: axis.maxNormal, constant: maxWorld });
  }

  return planes;
}

/**
 * "Tighten to selection" helper — return a clipping box that snaps to
 * the supplied selection bounds expressed in world coordinates,
 * normalized against the model bounds. Returns `null` if either input
 * is missing.
 */
export function tightenClipBoxToSelection(
  selectionBounds: ModelBounds | null,
  modelBounds: ModelBounds | null,
): ClippingBoxSpec | null {
  if (!selectionBounds || !modelBounds) return null;
  const [scx, scy, scz] = selectionBounds.center;
  const [ssx, ssy, ssz] = selectionBounds.size;
  const [mcx, mcy, mcz] = modelBounds.center;
  const [msx, msy, msz] = modelBounds.size;

  if (msx <= 0 || msy <= 0 || msz <= 0) return null;

  const fracMin = (centerSel: number, sizeSel: number, centerModel: number, sizeModel: number): number => {
    const minWorld = centerSel - sizeSel / 2;
    const minRel = (minWorld - (centerModel - sizeModel / 2)) / sizeModel;
    return clampClipPosition(minRel);
  };
  const fracMax = (centerSel: number, sizeSel: number, centerModel: number, sizeModel: number): number => {
    const maxWorld = centerSel + sizeSel / 2;
    const maxRel = (maxWorld - (centerModel - sizeModel / 2)) / sizeModel;
    return clampClipPosition(maxRel);
  };

  return {
    xMin: fracMin(scx, ssx, mcx, msx),
    xMax: fracMax(scx, ssx, mcx, msx),
    yMin: fracMin(scy, ssy, mcy, msy),
    yMax: fracMax(scy, ssy, mcy, msy),
    zMin: fracMin(scz, ssz, mcz, msz),
    zMax: fracMax(scz, ssz, mcz, msz),
  };
}
